import {
  emptyRemoteLink,
  eventIdForTask,
  mapGoogleEvents,
  systemTimeZone,
  type Calendar,
  type GoogleEvent,
  type Task,
} from "@app/core";
import { GoogleSyncError, type GoogleEventWrite } from "@app/storage";
import type { CalendarApi } from "../provider.js";
import { applyUpdate } from "./apply-update.js";
import {
  ackEventGraph,
  buildEventBody,
  effectiveGoogleDirty,
  isGone,
  loadTaskGraph,
} from "./task-graph.js";
import type { PushOptions, PushOutcome, ReconcileDeps } from "./types.js";

/**
 * OUTBOUND: push local edits on a bound calendar to Google.
 *
 * The dirty set is the per-row fact `mut > google_acked_mut` over each task's
 * event graph (`effectiveGoogleDirty`) — no cursor, no skip set. Rows the same
 * pass's pull applied are self-acknowledged and simply are not dirty. Each task
 * that is fully handled — including "nothing to send" exits — is acknowledged
 * at the `mut` its content was read at; a task that fails is NOT, so exactly
 * the failures retry next pass (the old cursor held back to the oldest failure
 * re-sent every already-delivered row newer than it).
 *
 * The task set is re-read here rather than taken from the caller's pre-inbound
 * snapshot: the snapshot's remote link goes stale the moment the pull re-links
 * or creates a row, and acting on a stale link is how a task with an event got
 * a SECOND one. Every mutation below re-reads its row immediately before acting
 * for the same reason.
 */
export async function pushCalendar(
  api: CalendarApi,
  cal: Calendar,
  deps: ReconcileDeps,
  now: Date,
  options: PushOptions = {},
): Promise<PushOutcome> {
  const gcal = cal.external_calendar_id!;
  const floatingTimeZone = options.floatingTimeZone ?? systemTimeZone();
  const graph = await loadTaskGraph(deps);
  let changed = false;
  let conflicts = 0;
  let inserted = 0;
  let patched = 0;
  let deleted = 0;
  let failures = 0;

  /** Re-read a task immediately before mutating it, so we never act on a copy
   *  another pass (or the inbound half of this one) has since changed. */
  const fresh = (id: string) => deps.tasks.findById(id);

  /** Identity-changing link writes bump `mut` by exactly one each; counting our
   *  own bumps is what lets the final acknowledgement land exactly on the mut we
   *  produced — while an interleaved USER edit exceeds the expectation and keeps
   *  the row dirty. Returns the bump (0 for an etag-only adopt). */
  const adoptLink = async (
    id: string,
    ev: { id?: string; etag?: string; iCalUID?: string },
    prior: Task,
  ): Promise<number> => {
    const r = await deps.tasks.setRemoteLink(id, {
      external_id: ev.id ?? prior.external_id,
      ical_uid: ev.iCalUID ?? prior.ical_uid,
      etag: ev.etag ?? null,
      source: "google",
      external_calendar_id: gcal,
    });
    return r.identityChanged ? 1 : 0;
  };

  const unlink = async (id: string): Promise<number> => {
    const r = await deps.tasks.setRemoteLink(id, emptyRemoteLink());
    return r.identityChanged ? 1 : 0;
  };

  // Insert a fresh remote event and adopt the assigned id/etag/uid. Re-checks the
  // link first: if one appeared while this pass ran, PATCH instead — an
  // unconditional insert here is the classic duplicate-event bug.
  // Returns the number of own link-write mut bumps it made.
  const insertAndAdopt = async (t: Task, body: GoogleEventWrite): Promise<number> => {
    const current = await fresh(t.id);
    if (!current) return 0;
    if (current.external_id && current.external_calendar_id === gcal) {
      const ev = await api.patchEvent(gcal, current.external_id, body, current.etag);
      const bump = await adoptLink(t.id, ev, current);
      changed = true;
      patched++;
      return bump;
    }
    // IDEMPOTENT CREATE. The event id is derived from the task id plus its link
    // GENERATION, so a second device holding the same unlinked task cannot mint a
    // second event: its insert collides (409) and it adopts the first device's
    // event instead. Without this, two devices that both push before either syncs
    // leave two events for one task, and no downstream merge can repair that —
    // picking a winner just orphans the loser, which the next pull imports as a
    // new task. The generation is what keeps re-creation (restore, re-date,
    // calendar move) idempotent too: each unlink bumps it, so the new event gets
    // a fresh id rather than colliding with its own tombstone.
    const derivedId = eventIdForTask(t.id, current.link_rev ?? 0);
    if (derivedId) {
      try {
        const ev = await api.insertEvent(gcal, { ...body, id: derivedId });
        const bump = await adoptLink(t.id, ev, current);
        changed = true;
        inserted++;
        return bump;
      } catch (e) {
        if (!isConflict(e)) throw e;
        // 409 means SOMETHING holds this id. Only a confirmed cancelled//gone
        // event lets us give up on determinism — a transient read failure must
        // NOT, or we would insert a randomly-identified duplicate, which is
        // precisely what this whole path exists to prevent. `adoptConflicting`
        // rethrows anything else so the task simply retries next pass.
        const existing = await adoptConflictingEvent(api, gcal, derivedId);
        if (existing) {
          const bump = await adoptLink(t.id, existing, current);
          changed = true;
          inserted++;
          return bump;
        }
        // The id belongs to a CANCELLED event and Google won't let us reuse it —
        // fall through and let Google assign a fresh one.
      }
    }
    const ev = await api.insertEvent(gcal, body);
    const bump = await adoptLink(t.id, ev, current);
    changed = true;
    inserted++;
    return bump;
  };

  const dirty = graph.all.filter(
    (t) =>
      !t.virtual &&
      t.calendar_id === cal.id &&
      // Top-level tasks always qualify; dated (detached) subtasks get their own event.
      (!t.subtask_parent_id || !!t.due_at) &&
      // Dirtiness spans the whole event body: the task, its rule, that rule's
      // overrides, and its undated subtree. Using the task row alone meant a
      // capped series (`until`) or a cancelled occurrence (`EXDATE`) never
      // reached Google — so the original series kept running next to the new one.
      effectiveGoogleDirty(graph, t),
  );

  for (const t of dirty) {
    // One bad item must never abort the whole push pass — count it and move on.
    // A failed task is simply NOT acknowledged, so it (alone) retries next pass.
    try {
      const body = buildEventBody(graph, t, floatingTimeZone);

      if (t.deleted_at) {
        const current = await fresh(t.id);
        if (!current?.deleted_at) continue; // un-deleted meanwhile — leave it alone, no ack
        let bumps = 0;
        if (current.external_id) {
          try {
            await api.deleteEvent(
              current.external_calendar_id ?? gcal,
              current.external_id,
              current.etag,
            );
          } catch (e) {
            if (!isGone(e)) throw e; // 404/410 = already gone = success
          }
          // Clear the remote link so a later RESTORE re-CREATES a fresh event
          // instead of PATCHing the now-deleted one (whose stale etag 412s, and a
          // re-fetched cancelled event maps as a tombstone — nothing syncs back).
          bumps = await unlink(t.id);
          changed = true;
          deleted++;
        }
        // A tombstone with no remote event is a "nothing to send" exit — ack it,
        // or it stays dirty and is re-scanned forever.
        await ackEventGraph(deps, graph, t, bumps);
        continue;
      }

      if (!t.due_at) {
        // Task moved to the inbox (became undated) — delete the remote event and
        // clear the link so it isn't re-pushed as a new event later.
        const current = await fresh(t.id);
        if (!current || current.due_at) continue; // re-dated meanwhile — no ack, retry
        let bumps = 0;
        if (current.external_id && current.external_calendar_id === gcal) {
          try {
            await api.deleteEvent(gcal, current.external_id, current.etag);
          } catch (e) {
            if (!isGone(e)) throw e;
          }
          bumps = await unlink(t.id);
          changed = true;
          deleted++;
        }
        await ackEventGraph(deps, graph, t, bumps);
        continue;
      }

      const current = await fresh(t.id);
      if (!current) continue;

      if (!current.external_id) {
        const bumps = await insertAndAdopt(t, body);
        await ackEventGraph(deps, graph, t, bumps);
        continue;
      }
      // MOVED here from another Google calendar (the link still points at the old
      // one). Patching THIS calendar with the old event id 404s — delete the old
      // remote event, then re-create here.
      if (current.external_calendar_id && current.external_calendar_id !== gcal) {
        try {
          await api.deleteEvent(current.external_calendar_id, current.external_id, current.etag);
        } catch (e) {
          if (!isGone(e)) throw e;
        }
        let bumps = await unlink(t.id);
        bumps += await insertAndAdopt(t, body);
        await ackEventGraph(deps, graph, t, bumps);
        continue;
      }
      try {
        const ev = await api.patchEvent(gcal, current.external_id, body, current.etag);
        const bumps = await adoptLink(t.id, ev, current);
        changed = true;
        patched++;
        await ackEventGraph(deps, graph, t, bumps);
      } catch (e) {
        if (e instanceof GoogleSyncError && e.status === 412) {
          const outcome = await resolvePreconditionFailure(api, gcal, deps, current, body, now);
          let bumps = outcome.ownWrites;
          if (outcome.kind === "recreate") {
            // Deleted in Google while we were editing → re-create rather than
            // resurrect a cancelled event. Goes through `insertAndAdopt` so the
            // new event gets the DERIVED id (the unlink bumped the generation):
            // a peer racing the same re-create 409s and adopts instead of
            // minting a second, randomly-named event.
            bumps += await unlink(t.id);
            bumps += await insertAndAdopt(t, body);
          }
          conflicts++;
          changed = true;
          patched++;
          await ackEventGraph(deps, graph, t, bumps);
        } else if (isGone(e)) {
          // The remote event was deleted but the local task was edited → re-create
          // from the local copy (clearing the stale id) instead of failing forever.
          let bumps = await unlink(t.id);
          bumps += await insertAndAdopt(t, body);
          await ackEventGraph(deps, graph, t, bumps);
        } else {
          throw e;
        }
      }
    } catch {
      failures++;
    }
  }

  // Undated subtasks that still carry an external_id from a prior standalone life
  // must have their Google event deleted (the subtask now lives only in the
  // parent's managed notes block). Covers (a) a top-level task just nested under a
  // parent (nestUnder clears due_at), (b) a dated subtask that became undated
  // before a sync could run. Bounded to dirty rows; includes deleted ones so a
  // task nested then immediately deleted is still cleaned up.
  const toDeleteEvent = graph.all.filter(
    (t) =>
      !t.virtual &&
      !!t.subtask_parent_id &&
      !t.due_at &&
      !!t.external_id &&
      t.external_calendar_id === gcal &&
      effectiveGoogleDirty(graph, t),
  );
  for (const t of toDeleteEvent) {
    try {
      const current = await fresh(t.id);
      if (!current?.external_id || current.due_at || !current.subtask_parent_id) continue;
      try {
        await api.deleteEvent(gcal, current.external_id, current.etag);
      } catch (e) {
        if (!isGone(e)) throw e; // 404/410 = already gone = success
      }
      const bumps = await unlink(t.id);
      // The row's own graph is its parent's concern; ack just the row itself.
      await deps.tasks.ackMut(t.id, "google", (t.mut ?? 0) + bumps);
      changed = true;
      deleted++;
    } catch {
      failures++;
    }
  }

  return {
    changed,
    conflicts,
    failures,
    stats: { dirty: dirty.length, inserted, patched, deleted, failures },
  };
}

/** 409 = an event with our derived id already exists on this calendar. */
function isConflict(e: unknown): boolean {
  return e instanceof GoogleSyncError && e.status === 409;
}

/**
 * Fetch the event our derived id collided with.
 *
 * Returns null ONLY for a confirmed dead event — a cancelled tombstone, or a
 * 404/410 — whose id Google will not let us take over; the caller then falls
 * back to a Google-assigned id. Every other failure (network, 5xx, auth)
 * RETHROWS, so the task is counted as a failure and retried on the next pass.
 * Swallowing those meant a transient blip turned into a second, randomly-named
 * event for a task that already had one.
 */
async function adoptConflictingEvent(
  api: CalendarApi,
  gcal: string,
  eventId: string,
): Promise<GoogleEvent | null> {
  try {
    const existing = await api.getEvent(gcal, eventId);
    return existing.status === "cancelled" ? null : existing;
  } catch (e) {
    if (isGone(e)) return null;
    throw e;
  }
}

/**
 * A 412 means our `If-Match` etag was stale. Local ALWAYS wins here: the push
 * only ever reaches a task the user edited on this device since the last
 * successful push, and the previous behaviour (refetch, overwrite local from
 * Google) is exactly what silently reverted a just-moved task.
 *
 * Re-read the event for a current etag and re-PATCH. If the remote genuinely
 * diverged, the shadowed version stays in Google's own event history — the local
 * edit would not be recoverable anywhere. When the event has vanished entirely,
 * fall back to adopting whatever Google now holds so the two don't drift.
 *
 * Returns `kind: "recreate"` when the remote event is CANCELLED — the caller
 * unlinks and re-creates through its idempotent-create path (derived id), so a
 * peer racing the same re-create adopts instead of duplicating. `"resolved"`
 * means the conflict was settled here (re-PATCH, or adopting Google's copy).
 * `ownWrites` counts the identity-changing link writes made here, for the
 * caller's acknowledgement arithmetic.
 */
async function resolvePreconditionFailure(
  api: CalendarApi,
  gcal: string,
  deps: ReconcileDeps,
  current: Task,
  body: GoogleEventWrite,
  now: Date,
): Promise<{ kind: "resolved" | "recreate"; ownWrites: number }> {
  const remote = await api.getEvent(gcal, current.external_id!);
  if (remote.status === "cancelled") return { kind: "recreate", ownWrites: 0 };
  try {
    const ev = await api.patchEvent(gcal, current.external_id!, body, remote.etag);
    const r = await deps.tasks.setRemoteLink(current.id, {
      external_id: ev.id ?? current.external_id,
      ical_uid: ev.iCalUID ?? current.ical_uid,
      etag: ev.etag ?? null,
      source: "google",
      external_calendar_id: gcal,
    });
    return { kind: "resolved", ownWrites: r.identityChanged ? 1 : 0 };
  } catch (e) {
    if (!(e instanceof GoogleSyncError && e.status === 412)) throw e;
    // Two 412s in a row means the event is changing under us faster than we can
    // write. Stop fighting: adopt Google's copy so both sides agree, rather than
    // looping. The local edit is still in undo/Trash-free history via the task's
    // own `updated_at` chain on other devices. `applyUpdate` self-acknowledges
    // its writes, so no own-write accounting is needed for it here.
    const mapped = mapGoogleEvents([remote], {
      now: () => now,
      calendarId: current.calendar_id ?? "",
      externalCalendarId: gcal,
    });
    const mt = mapped.assembly.tasks[0];
    if (mt) {
      const mrule = mt.recurrence_id
        ? mapped.assembly.rules.find((r) => r.id === mt.recurrence_id)
        : undefined;
      await applyUpdate(deps, current, mt, mrule, mapped.assembly.overrides, now);
    }
    return { kind: "resolved", ownWrites: 0 };
  }
}
